import React from 'react';
import { IntroductionTextarea } from './introduction-textarea';
import { BodySection } from './body-section';
import { ConclusionTextarea } from './conclusion-textarea';
import { SaveStatusIndicator } from './save-status-indicator';
import { BodyParagraphData, BodyParagraphType } from '@/types/writing';
import { SlashTriggerContext } from '@/types/slash-commands';
type SaveStatus = 'idle' | 'saving' | 'saved' | 'error';

interface EssayEditorContentProps {
  hasSelectedTask: boolean;
  introduction: string;
  bodyParagraphs: BodyParagraphData[];
  conclusion: string;
  onIntroductionChange: (value: string) => void;
  onConclusionChange: (value: string) => void;
  onBodyParagraphChange: (idx: number, value: string) => void;
  onBodyTypeChange: (idx: number, type: BodyParagraphType) => void;
  onAddBodyParagraph: () => void;
  onRemoveBodyParagraph: (idx: number) => void;
  isSlashMenuOpen: boolean;
  handleSlashTrigger: (context: SlashTriggerContext) => void;
  handleSlashFilterUpdate: (filterText: string) => void;
  handleSlashClose: () => void;
  handleTextareaKeyDown: (
    event: React.KeyboardEvent<HTMLTextAreaElement>
  ) => void;
  saveStatus: SaveStatus;
  totalWordCount: number;
  currentContent: string;
  initialPrompt: string;
}

export function EssayEditorContent({
  hasSelectedTask,
  introduction,
  bodyParagraphs,
  conclusion,
  onIntroductionChange,
  onConclusionChange,
  onBodyParagraphChange,
  onBodyTypeChange,
  onAddBodyParagraph,
  onRemoveBodyParagraph,
  isSlashMenuOpen,
  handleSlashTrigger,
  handleSlashFilterUpdate,
  handleSlashClose,
  handleTextareaKeyDown,
  saveStatus,
  totalWordCount,
  currentContent,
  initialPrompt,
}: EssayEditorContentProps) {
  if (!hasSelectedTask) {
    return (
      <div className='flex items-center justify-center h-full'>
        <div className='text-center text-muted-foreground'>
          Select a task from the right panel to begin writing
        </div>
      </div>
    );
  }

  return (
    <>
      <IntroductionTextarea
        value={introduction}
        onChange={onIntroductionChange}
        isSlashMenuOpen={isSlashMenuOpen}
        handleSlashTrigger={handleSlashTrigger}
        handleSlashFilterUpdate={handleSlashFilterUpdate}
        handleSlashClose={handleSlashClose}
        handleTextareaKeyDown={handleTextareaKeyDown}
        className='min-h-[100px]'
      />
      <BodySection
        paragraphs={bodyParagraphs}
        onParagraphChange={onBodyParagraphChange}
        onTypeChange={onBodyTypeChange}
        onAdd={onAddBodyParagraph}
        onRemove={onRemoveBodyParagraph}
        isSlashMenuOpen={isSlashMenuOpen}
        handleSlashTrigger={handleSlashTrigger}
        handleSlashFilterUpdate={handleSlashFilterUpdate}
        handleSlashClose={handleSlashClose}
        handleTextareaKeyDown={handleTextareaKeyDown}
      />
      <ConclusionTextarea
        value={conclusion}
        onChange={onConclusionChange}
        isSlashMenuOpen={isSlashMenuOpen}
        handleSlashTrigger={handleSlashTrigger}
        handleSlashFilterUpdate={handleSlashFilterUpdate}
        handleSlashClose={handleSlashClose}
        handleTextareaKeyDown={handleTextareaKeyDown}
        className='min-h-[100px]'
      />
      <div className='flex flex-row items-center gap-2 flex-shrink-0 pt-2'>
        <SaveStatusIndicator
          saveStatus={saveStatus}
          totalWordCount={totalWordCount}
          currentContent={currentContent}
          initialPrompt={initialPrompt}
        />
      </div>
    </>
  );
}
